import { useEffect, useRef, useState } from "react";

/**
 * Lightweight signature pad. No external dependency.
 * - Mouse + touch + pen events.
 * - Detects pointer / canvas support; if drawing isn't usable, parent should fall back to typed-only sign-off.
 * - Emits a PNG data URL via onChange when the user lifts their pen.
 */
export default function SignaturePad({ value, onChange, disabled, height = 160 }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef(null);
  const [empty, setEmpty] = useState(true);

  // Resize canvas to its CSS box (handles HiDPI for crisp strokes)
  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const resize = () => {
      const rect = wrapRef.current.getBoundingClientRect();
      const dpr = Math.max(window.devicePixelRatio || 1, 1);
      cvs.width = Math.round(rect.width * dpr);
      cvs.height = Math.round(height * dpr);
      cvs.style.width = `${Math.round(rect.width)}px`;
      cvs.style.height = `${height}px`;
      const ctx = cvs.getContext("2d");
      ctx.scale(dpr, dpr);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = 2;
      ctx.strokeStyle = "#0f172a";
      // If we already have an image, restore it.
      if (value) {
        const img = new Image();
        img.onload = () => {
          ctx.clearRect(0, 0, rect.width, height);
          ctx.drawImage(img, 0, 0, rect.width, height);
        };
        img.src = value;
        setEmpty(false);
      }
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height]);

  const ptFromEvent = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const pt = e.touches ? e.touches[0] : e;
    return { x: pt.clientX - rect.left, y: pt.clientY - rect.top };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = ptFromEvent(e);
  };
  const move = (e) => {
    if (!drawingRef.current || disabled) return;
    e.preventDefault();
    const p = ptFromEvent(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    setEmpty(false);
  };
  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onChange?.(dataUrl);
  };

  const clear = () => {
    const cvs = canvasRef.current;
    const ctx = cvs.getContext("2d");
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, cvs.width, cvs.height);
    ctx.restore();
    setEmpty(true);
    onChange?.(null);
  };

  return (
    <div className="rounded-md border border-slate-300 bg-white" ref={wrapRef} data-testid="signature-pad">
      <canvas
        ref={canvasRef}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        className="block w-full touch-none rounded-t-md bg-[linear-gradient(0deg,#f8fafc,white)]"
        data-testid="signature-canvas"
      />
      <div className="flex items-center justify-between border-t border-slate-200 px-3 py-1.5">
        <span className="text-xs text-slate-500">
          {empty ? "Sign above using your finger, stylus or mouse." : "Signature captured."}
        </span>
        <button
          type="button"
          onClick={clear}
          disabled={disabled || empty}
          data-testid="signature-clear-btn"
          className="text-xs font-semibold text-teal-700 hover:underline disabled:text-slate-300 disabled:no-underline"
        >
          Clear
        </button>
      </div>
    </div>
  );
}
